import * as React from 'react';
import { Redirect, Route, RouteProps } from 'react-router';
import { RoutesFun } from '../../schemas';
import { authService } from '../../services';
import { useSelector } from 'react-redux';
import { userState } from '../../store';

export interface ProtectedRouteProps extends RouteProps {
  isAuthenticated?: boolean;
  authenticationPath?: string;
  requiredMaxRoleId?: number; // Maximum allowed role ID (inclusive)
  requiredMinRoleId?: number; // Minimum allowed role ID (inclusive)
  allowedRoleIds?: number[]; // Specific allowed role IDs
  unauthorizedRedirectPath?: string; // Custom redirect path for unauthorized access
  unauthorizedComponent?: React.ComponentType<any>; // Component to render for unauthorized access
}

export class ProtectedRoute extends Route<ProtectedRouteProps> {
  public render() {
    const Routes = RoutesFun();
    const {
      requiredMaxRoleId,
      requiredMinRoleId,
      allowedRoleIds,
      unauthorizedRedirectPath,
      unauthorizedComponent,
      authenticationPath = Routes.Login,
      component: OriginalComponent,
      render: originalRender,
      ...routeProps
    } = this.props;

    // Check authentication first
    const hasAuth = authService.checkAuth();
    const hasLoginAccess = localStorage.getItem('LOGIN_ACCESS'); 
      
    if (!hasAuth || hasLoginAccess === 'true') {
      const renderComponent = () => (
        <Redirect to={{ pathname: authenticationPath }} />
      );
      return (
        <Route {...routeProps} component={renderComponent} render={undefined} />
      );
    }

    // If role-based checks are required, wrap the component
    if (
      requiredMaxRoleId !== undefined ||
      requiredMinRoleId !== undefined ||
      allowedRoleIds
    ) {
      const RoleProtectedComponent: React.FC<any> = (props) => {
        const user = useSelector(userState).userData;
        const userRoleId = user?.RoleId;

        // Helper function to render unauthorized access
        const renderUnauthorized = (): React.ReactElement => {
          if (unauthorizedComponent) {
            const UnauthorizedComponent = unauthorizedComponent;
            return <UnauthorizedComponent {...props} />;
          }
          if (unauthorizedRedirectPath) {
            return <Redirect to={{ pathname: unauthorizedRedirectPath }} />;
          }
          // Default fallback
          return <Redirect to={{ pathname: authenticationPath }} />;
        };

        // Check if user data is available
        if (!user || userRoleId === undefined || userRoleId === null) {
          return renderUnauthorized();
        }

        // Check specific allowed role IDs first (most restrictive)
        if (allowedRoleIds && allowedRoleIds.length > 0) {
          if (!allowedRoleIds.includes(userRoleId)) {
            return renderUnauthorized();
          }
        }

        // Check maximum role ID constraint
        if (requiredMaxRoleId !== undefined && userRoleId > requiredMaxRoleId) {
          return renderUnauthorized();
        }

        // Check minimum role ID constraint
        if (requiredMinRoleId !== undefined && userRoleId < requiredMinRoleId) {
          return renderUnauthorized();
        }

        // If all checks pass, render the original component
        if (OriginalComponent) {
          return <OriginalComponent {...props} />;
        }

        // Fallback to render prop if component is not provided
        if (originalRender) {
          const result = originalRender(props);

          return result ? <>{result}</> : <div />;
        }

        return <div />;
      };

      return (
        <Route
          {...routeProps}
          component={RoleProtectedComponent}
          render={undefined}
        />
      );
    }

    // If no role-based checks are required, render normally
    return <Route {...this.props} />;
  }
}
